/**
 * Kontekst dołączany do zgłoszeń błędów.
 *
 * Zgłoszenie „coś nie działa” bez tych danych zwykle kończy się wymianą pytań:
 * na którym ekranie, w jakiej przeglądarce, na jakiej szerokości. Zbieramy je
 * z góry — zwłaszcza ostatnie błędy z konsoli, których użytkownik nie zobaczy
 * i nie przepisze.
 */

/** Ile ostatnich błędów pamiętamy. Tyle wystarcza, żeby złapać ten właściwy. */
const ROZMIAR_BUFORA = 10

/** Limit długości jednego wpisu — stos potrafi mieć kilka ekranów. */
const LIMIT_WPISU = 400

const bufor: string[] = []

function zapamietaj(tekst: string): void {
  const wpis = tekst.slice(0, LIMIT_WPISU)
  bufor.push(wpis)
  // Bufor pierścieniowy: interesuje nas to, co wydarzyło się przed zgłoszeniem,
  // a nie cała historia sesji.
  if (bufor.length > ROZMIAR_BUFORA) bufor.shift()
}

/**
 * Podpina nasłuch błędów. Wołane raz, przy starcie aplikacji.
 *
 * Zwraca funkcję odpinającą — potrzebną w testach, żeby jeden przypadek nie
 * zostawiał nasłuchu następnemu.
 */
export function zbierajBledy(): () => void {
  const naBlad = (event: ErrorEvent) => zapamietaj(`${event.message} (${event.filename})`)
  const naOdrzucenie = (event: PromiseRejectionEvent) =>
    zapamietaj(`Nieobsłużone odrzucenie: ${String(event.reason)}`)

  window.addEventListener('error', naBlad)
  window.addEventListener('unhandledrejection', naOdrzucenie)

  // `console.error` łapiemy osobno: React zgłasza tędy błędy renderowania,
  // które nie docierają do `window.onerror`.
  const oryginalny = console.error
  console.error = (...argumenty: unknown[]) => {
    zapamietaj(argumenty.map((item) => String(item)).join(' '))
    oryginalny(...argumenty)
  }

  return () => {
    window.removeEventListener('error', naBlad)
    window.removeEventListener('unhandledrejection', naOdrzucenie)
    console.error = oryginalny
    bufor.length = 0
  }
}

/** Ostatnie błędy, od najstarszego. Pusty tekst, gdy nic się nie wydarzyło. */
export function ostatnieBledy(): string {
  return bufor.join('\n')
}

export interface KontekstZgloszenia {
  page_url: string
  app_version: string
  user_agent: string
  viewport: string
  console_errors: string | null
}

/** Wersja aplikacji podstawiana przy budowaniu; `dev` przy pracy lokalnej. */
export const WERSJA_APLIKACJI = __APP_VERSION__

export function zbierzKontekst(): KontekstZgloszenia {
  return {
    page_url: `${window.location.pathname}${window.location.search}`,
    app_version: WERSJA_APLIKACJI,
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}×${window.innerHeight}`,
    console_errors: ostatnieBledy() || null,
  }
}
